import { useEffect, useState, useCallback } from "react";
import { Search, Printer, User } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageTitle } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OpReceipt from "@/components/op/OpReceipt";

const MOD = { PENGAMBILAN: "Pengambilan", SETORAN: "Setoran", RIJEK_SUSULAN: "Rijek Susulan", RIJEK_KIRIM: "Kirim Perbaikan", RIJEK_TERIMA: "Terima Perbaikan" };
const MARKER = {
  REVISI: { t: "REVISI", c: "bg-blue-100 text-blue-700" },
  DIKOREKSI: { t: "SUDAH DIKOREKSI", c: "bg-amber-100 text-amber-700" },
  DIBATALKAN: { t: "DIBATALKAN", c: "bg-rose-100 text-rose-700" },
};
const STATUS_C = { POSTED: "bg-emerald-100 text-emerald-700", CANCELLED: "bg-rose-100 text-rose-700", CORRECTED: "bg-amber-100 text-amber-700" };

export default function InputOperator() {
  const [rows, setRows] = useState([]);
  const [module, setModule] = useState("");
  const [search, setSearch] = useState("");
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(() => {
    const p = module ? `?module=${module}&limit=300` : "?limit=300";
    api.get(`/op/history${p}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [module]);
  useEffect(() => { load(); }, [load]);

  const open = (op_no) => api.get(`/op/receipt/${op_no}`).then((r) => setReceipt(r.data)).catch(() => {});

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((h) => [h.op_no, h.worker_name, h.worker_id, h.entry_by].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
    : rows;

  return (
    <div data-testid="input-operator">
      <PageTitle title="Input Operator" desc="Riwayat seluruh input modul jahitan oleh operator pencatat — siapa yang menginput, kapan, dan untuk pengepul mana." />

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8" placeholder="Cari No. Struk / pengepul / operator…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="io-search" />
          </div>
          <Select value={module || "ALL"} onValueChange={(v) => setModule(v === "ALL" ? "" : v)}>
            <SelectTrigger data-testid="io-module"><SelectValue placeholder="Semua Modul" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Modul</SelectItem>
              {Object.entries(MOD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="io-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
                {["Tanggal", "No. Struk", "Modul", "Pengepul", "Diinput oleh", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => {
                const mk = MARKER[h.revision_marker];
                return (
                  <tr key={h.op_no} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`io-row-${h.op_no}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{fmtDate(h.date)}</td>
                    <td className="px-3 py-2.5 mono text-xs font-semibold text-blue-700">{h.op_no}</td>
                    <td className="px-3 py-2.5 text-slate-700">{MOD[h.source_module] || h.source_module}</td>
                    <td className="px-3 py-2.5"><span className="mono text-xs text-slate-500">{h.worker_id || "-"}</span> <span className="text-slate-700">{h.worker_name || ""}</span></td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800" data-testid={`io-entry-${h.op_no}`}>
                        <User className="w-3.5 h-3.5 text-slate-400" />{h.entry_by || "-"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_C[h.status] || "bg-slate-100 text-slate-600"}`}>{h.status}</span>
                        {mk && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${mk.c}`}>{mk.t}</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {!["RIJEK_KIRIM", "RIJEK_TERIMA"].includes(h.source_module) && (
                        <button onClick={() => open(h.op_no)} className="inline-flex items-center gap-1 text-sm text-blue-700" data-testid={`io-print-${h.op_no}`}><Printer className="w-4 h-4" />Struk</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">Belum ada input operator.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {receipt && <OpReceipt doc={receipt} onClose={() => setReceipt(null)} />}
    </div>
  );
}
